import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassStudentSelect, TargetModeSelect } from "@/components/ClassStudentSelect";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { colorOf } from "@/lib/subjects";
import { Markdown } from "@/lib/markdown";
import { toast } from "sonner";
import { Trophy, Plus, Trash2, Gauge, ClipboardCheck, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const todayStr = () => new Date().toISOString().slice(0, 10);
const netOf = (s) => (s.dogru || 0) - (s.yanlis || 0) / 3;
const band = (est) => est >= 450 ? { bg: "bg-emerald-50", text: "text-emerald-700", dot: "text-emerald-500" }
  : est >= 300 ? { bg: "bg-amber-50", text: "text-amber-700", dot: "text-amber-500" }
  : { bg: "bg-rose-50", text: "text-rose-700", dot: "text-rose-500" };

export default function Exams() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [mode, setMode] = useState(params.get("grade") ? "class" : "student");
  const [clsGrade, setClsGrade] = useState(Number(params.get("grade")) || 1);
  const [clsSection, setClsSection] = useState(params.get("section") || "");
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [students, setStudents] = useState([]);
  const [studentGrade, setStudentGrade] = useState(null);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", date: todayStr(), estimated_score: "", zone: "", rows: {} });

  const isAdmin = user?.role === "admin";
  const canEdit = user?.role === "admin" || user?.role === "koc";

  useEffect(() => {
    if (isAdmin) api.get("/students").then(({ data }) => setStudents(data));
  }, [isAdmin]);

  useEffect(() => {
    if (studentId) return;
    if (isAdmin) return;
    api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setStudentName(data.student_name || ""); });
  }, [studentId, isAdmin]);

  useEffect(() => {
    const grade = students.find((s) => s.id === studentId)?.grade ?? (mode === "class" ? clsGrade : studentGrade);
    api.get("/subjects", { params: grade ? { grade } : {} }).then(({ data }) => setSubjects(data));
  }, [studentId, students, studentGrade, mode, clsGrade]);

  const load = useCallback(async () => {
    if (!studentId) { setExams([]); return; }
    const { data } = await api.get("/exams", { params: { student_id: studentId } });
    setExams(data);
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    const rows = {};
    subjects.forEach((s) => { rows[s.name] = { dogru: "", yanlis: "", bos: "" }; });
    setForm({ name: "", date: todayStr(), estimated_score: "", zone: "", rows });
    setOpen(true);
  };

  const setRow = (subj, field, val) => setForm((f) => ({ ...f, rows: { ...f.rows, [subj]: { ...f.rows[subj], [field]: val } } }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Sınav adı gerekli");
    const subs = Object.entries(form.rows)
      .map(([subject, v]) => ({ subject, dogru: Number(v.dogru) || 0, yanlis: Number(v.yanlis) || 0, bos: Number(v.bos) || 0 }))
      .filter((s) => s.dogru || s.yanlis || s.bos);
    try {
      if (mode === "class") {
        const { data } = await api.post("/exams/assign-class", {
          grade: clsGrade, class_section: clsSection || null, name: form.name.trim(), date: form.date,
          subjects: subs, estimated_score: Number(form.estimated_score) || 0, zone: form.zone.trim(),
        });
        toast.success(`${data.count} öğrenciye zirve sonucu eklendi ✓`);
        setOpen(false);
        return;
      }
      await api.post("/exams", {
        student_id: studentId, name: form.name.trim(), date: form.date,
        subjects: subs, estimated_score: Number(form.estimated_score) || 0, zone: form.zone.trim(),
      });
      toast.success("Zirve sonucu eklendi ✓");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/exams/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // radar: avg net per subject
  const subjNet = {};
  exams.forEach((e) => (e.subjects || []).forEach((s) => {
    subjNet[s.subject] = subjNet[s.subject] || { total: 0, n: 0 };
    subjNet[s.subject].total += netOf(s); subjNet[s.subject].n += 1;
  }));
  const radarData = Object.entries(subjNet).map(([subject, v]) => ({ subject, net: Math.round((v.total / v.n) * 10) / 10 }));

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 no-print">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-100 text-purple-600"><Trophy className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Zirve Deneme Sonuçları</h1>
            <p className="text-slate-500">Ders bazlı doğru/yanlış/boş girişi, otomatik net ve gelişim takibi.{studentName ? ` · ${studentName}` : ""}</p>
          </div>
        </div>
        {canEdit && (studentId || mode === "class") && (
          <button data-testid="add-exam-btn" onClick={openAdd} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 font-semibold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700 active:scale-95">
            <Plus className="h-5 w-5" /> Zirve Sonucu Ekle
          </button>
        )}
      </div>

      {isAdmin && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm no-print">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Öğrenci Seç</label>
          <TargetModeSelect students={students} mode={mode} setMode={setMode} studentId={studentId} setStudentId={(v) => { setStudentId(v); setStudentName(students.find((s) => s.id === v)?.name || ""); }} grade={clsGrade} setGrade={setClsGrade} section={clsSection} setSection={setClsSection} testid="exam-student-select" studentPlaceholder="Seçiniz…" />
        </div>
      )}

      {radarData.length >= 3 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-2 flex items-center gap-2 font-display text-xl font-bold text-slate-900"><TrendingUp className="h-5 w-5 text-indigo-500" /> Ders Bazlı Net (Ortalama)</h3>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12, fill: "#334155" }} />
                <PolarRadiusAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <Radar name="Net" dataKey="net" stroke="#6366f1" fill="#6366f1" fillOpacity={0.35} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {!studentId ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Öğrenci seçin.</div>
      ) : exams.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Henüz zirve sonucu eklenmedi.</div>
      ) : (
        <div className="space-y-5">
          {exams.map((e, i) => <ExamCard key={e.id} exam={e} prev={exams[i - 1]} canEdit={canEdit} onDelete={remove} />)}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Zirve Sonucu Gir</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Sınav Adı</label><input data-testid="exam-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ör. 1. Zirve" className={inp} /></div>
              <div><label className={lbl}>Tarih</label><input data-testid="exam-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inp} /></div>
            </div>
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">LGS puanı MEB 2026 yöntemiyle aynı adlı denemeye giren tüm öğrencilerin netlerinden otomatik hesaplanır (elle girilmez).</p>
            <div className="grid grid-cols-1 gap-3">
              <div><label className={lbl}>Bölge / Not</label><input data-testid="exam-zone-input" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="ör. İlk %50" className={inp} /></div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="mb-2 grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 text-xs font-bold uppercase text-slate-400">
                <span>Ders</span><span className="w-14 text-center">Doğru</span><span className="w-14 text-center">Yanlış</span><span className="w-14 text-center">Boş</span>
              </div>
              {subjects.length === 0 ? <p className="py-3 text-center text-sm text-slate-400">Bu sınıf için ders tanımlı değil.</p> : subjects.map((s) => {
                const c = colorOf(s.color);
                return (
                  <div key={s.id} className="mb-2 grid grid-cols-[1fr_auto_auto_auto] items-center gap-2">
                    <span className={`flex items-center gap-1.5 text-sm font-semibold ${c.text}`}><span className={`h-2 w-2 rounded-full ${c.dot}`} />{s.name}</span>
                    <input data-testid={`exam-d-${s.name}`} type="number" min={0} value={form.rows[s.name]?.dogru ?? ""} onChange={(e) => setRow(s.name, "dogru", e.target.value)} className="w-14 rounded-lg border border-slate-200 px-2 py-1.5 text-center outline-none focus:border-emerald-400" />
                    <input data-testid={`exam-y-${s.name}`} type="number" min={0} value={form.rows[s.name]?.yanlis ?? ""} onChange={(e) => setRow(s.name, "yanlis", e.target.value)} className="w-14 rounded-lg border border-slate-200 px-2 py-1.5 text-center outline-none focus:border-rose-400" />
                    <input data-testid={`exam-b-${s.name}`} type="number" min={0} value={form.rows[s.name]?.bos ?? ""} onChange={(e) => setRow(s.name, "bos", e.target.value)} className="w-14 rounded-lg border border-slate-200 px-2 py-1.5 text-center outline-none focus:border-slate-400" />
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <button data-testid="save-exam-btn" onClick={save} className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">Kaydet</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const lbl = "mb-1 block text-sm font-semibold text-slate-700";
const inp = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";

function ExamCard({ exam, prev, canEdit, onDelete }) {
  const puan = exam.lgs?.puan;
  const b = band(puan ?? 0);
  const delta = prev ? Math.round((exam.total_net - prev.total_net) * 100) / 100 : null;
  const DeltaIcon = delta == null ? Minus : delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const deltaColor = delta == null ? "text-slate-400" : delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-slate-400";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl font-extrabold text-slate-900">{exam.name}</h3>
          <div className="font-mono text-sm text-slate-400">{exam.date}</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-display text-4xl font-extrabold text-indigo-700">{exam.total_net?.toFixed(2)}</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Net</div>
          </div>
          {delta != null && (
            <div className={`text-right ${deltaColor}`}>
              <div className="flex items-center gap-1 font-bold"><DeltaIcon className="h-4 w-4" />{delta > 0 ? "+" : ""}{delta}</div>
              <div className="text-[11px] text-slate-400">önceki zirveye göre</div>
            </div>
          )}
          <button data-testid="telafi-btn" onClick={() => toast.info(`Telafı listesi: ${exam.total_yanlis} yanlış · ${exam.total_bos} boş soru tekrar planına eklendi`)} className="flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 active:scale-95 no-print">
            <ClipboardCheck className="h-4 w-4" /> Telafı Oluştur
          </button>
          {canEdit && <button data-testid={`delete-exam-${exam.id}`} onClick={() => onDelete(exam.id)} className="rounded-lg p-2 text-rose-500 transition hover:bg-rose-50 active:scale-90 no-print"><Trash2 className="h-4 w-4" /></button>}
        </div>
      </div>

      <div className={`mt-4 flex items-center gap-2 rounded-xl ${b.bg} px-4 py-2.5`}>
        <Gauge className={`h-5 w-5 ${b.dot}`} />
        <span className="text-sm font-semibold text-slate-600">LGS Puanı:</span>
        {puan != null ? <span data-testid={`zirve-puan-${exam.id}`} className={`text-lg font-extrabold ${b.text}`}>{puan}</span> : <span data-testid={`zirve-puan-${exam.id}`} className="text-xs font-semibold text-slate-500">{exam.lgs?.note || "Hesaplanamadı — netler geçerlidir"}</span>}
        {puan != null && <span className="rounded bg-white/70 px-2 py-0.5 text-[10px] font-bold text-slate-500">{exam.lgs.label}</span>}
        {exam.zone && <span className={`text-sm font-semibold ${b.text}`}>· {exam.zone}</span>}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Box value={exam.total_dogru} label="Doğru" cls="bg-emerald-50 text-emerald-700" />
        <Box value={exam.total_yanlis} label="Yanlış" cls="bg-rose-50 text-rose-700" />
        <Box value={exam.total_bos} label="Boş" cls="bg-slate-100 text-slate-600" />
      </div>

      {(exam.subjects || []).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {exam.subjects.map((s) => (
            <span key={s.subject} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
              {s.subject}: <span className="text-emerald-600">{s.dogru}D</span> <span className="text-rose-600">{s.yanlis}Y</span> <span className="text-slate-500">{s.bos}B</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Box({ value, label, cls }) {
  return (
    <div className={`rounded-2xl ${cls} py-4 text-center`}>
      <div className="text-3xl font-extrabold">{value ?? 0}</div>
      <div className="text-sm font-semibold">{label}</div>
    </div>
  );
}
